import {isFeatureEnabled} from '@github-ui/feature-flags'

/**
 * Generates the API path for repository tasks endpoint
 * @param nwo - Repository name with owner (e.g., "github/github")
 * @returns API path for the repo tasks endpoint
 */
export function repoTasksApiPath(nwo: string): string {
  const [owner, repo] = nwo.split('/')
  return `/agents/repos/${owner}/${repo}/tasks`
}

/**
 * Generates the API path for creating a session for a specific task
 * @param nwo - Repository name with owner (e.g., "github/github")
 * @param taskId - The task ID
 * @returns API path for the task sessions endpoint
 */
export function repoTaskSessionsApiPath(nwo: string, taskId: string): string {
  const [owner, repo] = nwo.split('/')
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}/sessions`
}

export type TasksQueryParams = {
  /** Optional user ID to filter tasks by creator */
  creatorId?: number
  /** Optional 1-based page number for paginated results. Only included when pagination is enabled (page is explicitly provided). */
  page?: number
  /** Optional boolean to filter archived tasks */
  archived?: boolean
  /** Optional boolean to include open and closed task counts in the response */
  includeCounts?: boolean
  /** Optional number of tasks per page */
  pageSize?: number
  /** Optional task type filter (e.g., 'review' for code review tasks only) */
  type?: string
  /** Optional agent IDs to filter tasks by agent */
  agentIds?: number[]
  /** When true, only returns tasks with an associated repository; when false, only returns tasks with no associated repository; when undefined, returns all tasks */
  withRepo?: boolean
  /** Optional custom agent name to filter tasks by exact match */
  customAgentName?: string
}

/**
 * Generates query parameters for tasks endpoints.
 * When copilot_task_api_github_rest_style is enabled, uses GitHub REST API-style param names:
 *   page (instead of page_number), per_page (instead of page_size)
 * @param params Query parameters
 * @returns Query string for tasks endpoint
 */
const generateTasksQueryParams = (params: TasksQueryParams): string => {
  const useRestStyle = isFeatureEnabled('copilot_task_api_github_rest_style')
  const searchParams = new URLSearchParams()

  if (params.creatorId !== undefined) {
    searchParams.set('creator_id', String(params.creatorId))
  }
  if (params.page !== undefined) {
    searchParams.set(useRestStyle ? 'page' : 'page_number', String(params.page))
  }
  if (params.archived !== undefined) {
    searchParams.set('archived', String(params.archived))
  }
  if (params.includeCounts !== undefined) {
    searchParams.set('include_counts', String(params.includeCounts))
  }
  if (params.pageSize !== undefined) {
    searchParams.set(useRestStyle ? 'per_page' : 'page_size', String(params.pageSize))
  }
  if (params.type !== undefined) {
    searchParams.set('type', params.type)
  }
  if (params.customAgentName !== undefined) {
    searchParams.set('custom_agent_name', params.customAgentName)
  }
  if (params.agentIds !== undefined && params.agentIds.length > 0) {
    searchParams.set('include_agent_ids', params.agentIds.join(','))
  }
  if (params.withRepo !== undefined) {
    searchParams.set('with_repo', String(params.withRepo))
  }

  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

/**
 * Generates the API path for the repository tasks endpoint with query parameters
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param params - Optional query parameters
 * @returns API path for the repo tasks endpoint with query string
 */
export function repoTasksApiPathWithParams(owner: string, repo: string, params?: TasksQueryParams): string {
  const basePath = `/agents/repos/${owner}/${repo}/tasks`
  const query = params ? generateTasksQueryParams(params) : ''
  return `${basePath}${query}`
}

/**
 * Generates the API path for the tasks endpoint with query parameters
 * @param params - Optional query parameters
 * @returns API path for the tasks endpoint with query string
 */
export function tasksApiPathWithParams(params?: TasksQueryParams): string {
  const basePath = `/agents/tasks`
  const query = params ? generateTasksQueryParams(params) : ''
  return query ? `${basePath}${query}` : basePath
}

/**
 * Generates the API path for task endpoint
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param taskId - Task ID
 * @returns API path for the task endpoint
 */
export function taskApiPath(owner: string, repo: string, taskId: string): string {
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}`
}

/**
 * Generates the API path for a task by ID only (no repo context required).
 * Used for repo-less tasks where owner/repo may not be known.
 * @param taskId - Task ID
 * @returns API path for the task endpoint
 */
export function taskByIdApiPath(taskId: string): string {
  return `/agents/tasks/${taskId}`
}

/**
 * Generates the API path for pull request sessions endpoint
 * @param pullId - Pull request ID
 * @returns API path for the pull sessions endpoint
 */
export function pullSessionsApiPath(pullId: number): string {
  return `/agents/sessions/resource/pull/${pullId}`
}

/**
 * Generates the API path for task inactive logs endpoint
 * @param owner - Repository owner
 * @param repo - Repository name
 * @param taskId - Task ID
 * @returns API path for the task inactive logs endpoint
 */
export function taskInactiveLogsApiPath(owner: string, repo: string, taskId: string): string {
  return `/agents/repos/${owner}/${repo}/tasks/${taskId}/inactive_logs`
}

/**
 * Generates the API path for pull request inactive logs endpoint
 * @param pullId - Pull request ID
 * @returns API path for the pull inactive logs endpoint
 */
export function pullInactiveLogsApiPath(pullId: number): string {
  return `/agents/sessions/resource/pull/${pullId}/inactive_logs`
}
